import * as React from 'react';
import { useFela } from 'react-fela';
import {
  RESOURCE_EDITOR_ID,
  BINARY_DIFF_EDITOR_ID,
  SIDE_BY_SIDE_EDITOR_ID,
  TEXT_DIFF_EDITOR_ID,
} from '../constants';
import { MonacoEditor, MonacoDiffEditor, IMonacoEditorTextProps, IMonacoDiffEditorProps } from '@workbench-stack/components/lib/editors';
import { Registry } from '@vscode-alt/monaco-editor/esm/vs/platform/registry/common/platform';
import { IEditorRegistry, CacheTypenames, IEditorContributionProps } from '@workbench-stack/core';
import { ClientExtensions } from '../constants';
import { EditorContributionFill, IEditorComponentProps } from './editor';
import { URI } from '@vscode-alt/monaco-editor/esm/vs/base/common/uri';
import { logger } from '@cdm-logger/client';

export type IEditorTextProps = IMonacoEditorTextProps & IEditorComponentProps;
export type IEditorDiffTextProps = IMonacoDiffEditorProps &
  IEditorComponentProps;

export type IEditorProps = IEditorTextProps | IEditorDiffTextProps;

// String Editor
export const ResourceEditor: React.FC<IEditorContributionProps> = (props) => {
  const { theme } = useFela();

  return (
    <EditorContributionFill>
      {(editorProps: IEditorTextProps) => {
        const uri = URI.parse(editorProps.input.resource);
        return editorProps.visibleEditorId === props.ID ? (
          <MonacoEditor
            context={editorProps.context}
            defaultValue={editorProps.defaultValue}
            doUpdateValue={editorProps.doUpdateValue}
            enableApi={editorProps.enableApi}
            editorDidMount={editorProps.editorDidMount}
            editorWillMount={editorProps.editorWillMount}
            getEditorState={editorProps.getEditorState}
            theme={editorProps.theme || theme}
            uri={uri}
            height={editorProps.height}
            width={editorProps.width}
            language={editorProps.language}
            onChange={editorProps.onChange}
            options={editorProps.options}
            value={editorProps.value}
            updateEditorState={editorProps.updateEditorState}
            requireConfig={editorProps.requireConfig}
            updateCursorState={editorProps.updateCursorState}
          />
        ) : null;
      }}
    </EditorContributionFill>
  );
};
ResourceEditor.defaultProps = { ID: RESOURCE_EDITOR_ID };
/**
 * Text diff editor id.
 */
export const TextDiffEditor: React.FC<IEditorContributionProps> = (props) => {
  const { theme } = useFela();

  return (
    <EditorContributionFill>
      {(editorProps: IEditorDiffTextProps) =>
        editorProps.visibleEditorId === props.ID ? (
          <MonacoDiffEditor theme={theme} {...editorProps} />
        ) : null
      }
    </EditorContributionFill>
  );
};
TextDiffEditor.defaultProps = { ID: TEXT_DIFF_EDITOR_ID };

/**
 * Binary diff editor.
 */
export const BinaryResourceDiffEditor: React.FC<IEditorContributionProps> = (
  props,
) => {
  const { theme } = useFela();

  return (
    <EditorContributionFill>
      {(editorProps: IEditorDiffTextProps) =>
        editorProps.visibleEditorId === props.ID ? (
          <MonacoDiffEditor theme={theme} {...editorProps} />
        ) : null
      }
    </EditorContributionFill>
  );
};
BinaryResourceDiffEditor.defaultProps = { ID: BINARY_DIFF_EDITOR_ID };

export const SideBySideEditor: React.FC<IEditorContributionProps> = (props) => {
  const { theme } = useFela();

  return (
    <EditorContributionFill>
      {(editorProps: IEditorDiffTextProps) =>
        editorProps.visibleEditorId === props.ID ? (
          <MonacoDiffEditor theme={theme} {...editorProps} />
        ) : null
      }
    </EditorContributionFill>
  );
};
SideBySideEditor.defaultProps = { ID: SIDE_BY_SIDE_EDITOR_ID };

export const EditorContribution = (props) => {
  try {
    Registry.as<IEditorRegistry>(ClientExtensions.Editors).registerEditor(
      {
        id: ResourceEditor.defaultProps.ID,
        name: ResourceEditor.displayName,
      },
      [
        { __typename: CacheTypenames.ResourceEditorInput },
        { __typename: CacheTypenames.UntitledEditorInput },
      ],
    );
  } catch (err) {
    logger.error('file registry failed');
    logger.error(err);
  }

  try {
    Registry.as<IEditorRegistry>(ClientExtensions.Editors).registerEditor(
      {
        id: TextDiffEditor.defaultProps.ID,
        name: ResourceEditor.displayName,
      },
      [{ __typename: CacheTypenames.DiffEditorInput }],
    );
  } catch (err) {
    logger.error('Registry failed for ', CacheTypenames.DiffEditorInput);
    logger.error(err);
  }
  try {
    Registry.as<IEditorRegistry>(ClientExtensions.Editors).registerEditor(
      {
        id: BinaryResourceDiffEditor.defaultProps.ID,
        name: ResourceEditor.displayName,
      },
      [{ __typename: CacheTypenames.DiffEditorInput }],
    );
  } catch (err) {
    logger.error('Registry failed for ', CacheTypenames.DiffEditorInput);
    logger.error(err);
  }
  try {
    Registry.as<IEditorRegistry>(ClientExtensions.Editors).registerEditor(
      {
        id: SideBySideEditor.defaultProps.ID,
        name: ResourceEditor.displayName,
      },
      [{ __typename: CacheTypenames.SideBySideEditorInput }],
    );
  } catch (err) {
    logger.error('Registry failed for ', CacheTypenames.SideBySideEditorInput);
    logger.error(err);

  }

  return (
    <>
      <ResourceEditor {...props} />
      <TextDiffEditor {...props} />
      <BinaryResourceDiffEditor {...props} />
      <SideBySideEditor {...props} />
    </>
  );
};
